/**
 * Job Assessment (Questions) Repository
 * Handles job assessment question CRUD API calls
 */

import { apiClient } from '../client'
import type { PaginatedResponse } from './role.repository'

export interface JobAssessmentSection {
  id: number
  name: string
  sort_order: number
}

export interface JobAssessmentQuestion {
  id: number
  section_id: number | null
  parent_id: number | null
  question_en: string | null
  question_ar: string | null
  type: string | null
  options: string | null
  options_ar?: string | null
  is_merchandise: boolean
  is_promoter: boolean
  is_L4: boolean
  is_L5: boolean
  status: string | null
  section?: JobAssessmentSection | null
  parent?: { id: number; question_en: string | null } | null
}

export interface CreateJobAssessmentQuestionRequest {
  section_id?: number | null
  parent_id?: number | null
  question_en?: string
  question_ar?: string
  type?: string
  options?: string
  options_ar?: string
  is_merchandise?: boolean
  is_promoter?: boolean
  is_L4?: boolean
  is_L5?: boolean
  status?: string
}

export interface UpdateJobAssessmentQuestionRequest {
  section_id?: number | null
  parent_id?: number | null
  question_en?: string
  question_ar?: string
  type?: string
  options?: string
  options_ar?: string
  is_merchandise?: boolean
  is_promoter?: boolean
  is_L4?: boolean
  is_L5?: boolean
  status?: string
}

export interface QuestionForParentDropdown {
  id: number
  question_en: string | null
}

interface BackendPaginatedResponse<T> {
  data: T[]
  pagination: { total: number; page: number; limit: number; pages: number }
}

class JobAssessmentRepository {
  async getAll(
    page: number = 1,
    limit: number = 10,
    filters?: {
      section_id?: number | string
      is_merchandise?: boolean | string
      is_promoter?: boolean | string
      is_L4?: boolean | string
      is_L5?: boolean | string
      status?: string
      type?: string
    }
  ): Promise<PaginatedResponse<JobAssessmentQuestion>> {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    })
    if (filters?.section_id !== undefined && filters?.section_id !== '') {
      params.append('section_id', String(filters.section_id))
    }
    if (filters?.is_merchandise !== undefined) {
      params.append('is_merchandise', String(filters.is_merchandise))
    }
    if (filters?.is_promoter !== undefined) {
      params.append('is_promoter', String(filters.is_promoter))
    }
    if (filters?.is_L4 !== undefined) {
      params.append('is_L4', String(filters.is_L4))
    }
    if (filters?.is_L5 !== undefined) {
      params.append('is_L5', String(filters.is_L5))
    }
    if (filters?.status) params.append('status', filters.status)
    if (filters?.type) params.append('type', filters.type)

    const res = await apiClient.get<BackendPaginatedResponse<JobAssessmentQuestion>>(
      `/job-assessment?${params.toString()}`
    )
    return {
      data: res.data,
      total: res.pagination.total,
      page: res.pagination.page,
      limit: res.pagination.limit,
      totalPages: res.pagination.pages,
    }
  }

  async getById(id: number): Promise<JobAssessmentQuestion> {
    return apiClient.get<JobAssessmentQuestion>(`/job-assessment/${id}`)
  }

  async create(data: CreateJobAssessmentQuestionRequest): Promise<JobAssessmentQuestion> {
    return apiClient.post<JobAssessmentQuestion>('/job-assessment', data)
  }

  async update(id: number, data: UpdateJobAssessmentQuestionRequest): Promise<JobAssessmentQuestion> {
    return apiClient.put<JobAssessmentQuestion>(`/job-assessment/${id}`, data)
  }

  async delete(id: number): Promise<{ message: string }> {
    return apiClient.delete<{ message: string }>(`/job-assessment/${id}`)
  }

  async getQuestionsForParent(sectionId?: number): Promise<QuestionForParentDropdown[]> {
    const url = sectionId != null
      ? `/job-assessment/for-parent?section_id=${sectionId}`
      : '/job-assessment/for-parent'
    return apiClient.get<QuestionForParentDropdown[]>(url)
  }
}

export const jobAssessmentRepository = new JobAssessmentRepository()

/** Sections for grouping assessment questions */
export interface CreateSectionRequest {
  name: string
  sort_order?: number
}

export interface UpdateSectionRequest {
  name?: string
  sort_order?: number
}

class JobAssessmentSectionRepository {
  async getAll(): Promise<JobAssessmentSection[]> {
    return apiClient.get<JobAssessmentSection[]>('/job-assessment/sections')
  }

  async getById(id: number): Promise<JobAssessmentSection> {
    return apiClient.get<JobAssessmentSection>(`/job-assessment/sections/${id}`)
  }

  async create(data: CreateSectionRequest): Promise<JobAssessmentSection> {
    const res = await apiClient.post<{ data: JobAssessmentSection }>('/job-assessment/sections', data)
    return (res as any)?.data ?? res
  }

  async update(id: number, data: UpdateSectionRequest): Promise<JobAssessmentSection> {
    return apiClient.put<JobAssessmentSection>(`/job-assessment/sections/${id}`, data)
  }

  async delete(id: number): Promise<{ message: string }> {
    return apiClient.delete<{ message: string }>(`/job-assessment/sections/${id}`)
  }
}

export const jobAssessmentSectionRepository = new JobAssessmentSectionRepository()
